import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { X, Copy, Mail, CheckCircle, Calendar, Video, MessageCircle, ArrowRight, Clock, Sparkles } from 'lucide-react';
import { triggerSubtleConfetti } from '../utils/confetti';
import { createWhatsAppUrl } from '../utils/whatsapp';
import { trackEvent, EVENTS } from '../utils/posthog';
import { useSafeT } from '../src/hooks/useSafeT';
// Email generation logic
type EmailParams = {
  companyName: string;
  jobTitle: string;
  responsibilities: string;
  recruiterName: string;
  recruiterEmail: string;
  recruiterPhone: string;
};
const TO_EMAIL = 'mona.sudarat31@gmail.com';
const CALENDAR_URL = 'https://calendar.app.google/G8vmY9BPADzAnVUJ7';
const WHATSAPP_PHONE = '+358458426661';
function sanitize(text: string = ''): string {
  return text.replace(/[<>]/g, '').replace(/\s{2,}/g, ' ').trim();
}
function generateRecruiterEmail(params: Partial<EmailParams>) {
  const company = sanitize(params.companyName || '');
  const title = sanitize(params.jobTitle || '');
  const resp = sanitize(params.responsibilities || '');
  const subjectParts = [title ? `${title}` : 'Healthcare Role', company ? `@ ${company}` : '', `— interest to connect`].filter(Boolean);
  const subject = subjectParts.join(' ');
  const intro = `Hi Mona,`;
  const line1 = `I came across your CV showcasing your Finnish nursing expertise and multicultural background. I'm interested in connecting with you for a${company ? ` role at ${company}` : ' healthcare position'}${title ? ` as ${title}` : ''}${resp ? ` focusing on ${resp}` : ''}.\n`;
  const line2 = `Your Finnish certifications and experience are particularly relevant for our needs. Would you be available for a brief discussion this week to explore this opportunity?`;
  const line3 = `I'd be happy to share more details about our team, work environment, and compensation.`;
  const signature = ['', 'Best regards,', sanitize(params.recruiterName) || 'Recruiter', sanitize(params.recruiterEmail) || '', sanitize(params.recruiterPhone) || ''].filter(Boolean).join('\n');
  const body = [intro, '', line1, line2, line3, signature].join('\n');
  return {
    subject,
    body: body.length > 1500 ? body.slice(0, 1497) + '...' : body
  };
}
function openMailClient(to: string, subject: string, body: string) {
  const mailtoUrl = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = mailtoUrl;
}
interface ConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const ConnectModal: React.FC<ConnectModalProps> = ({
  isOpen,
  onClose
}) => {
  const t = useSafeT();
  const [selectedOption, setSelectedOption] = useState<'booking' | 'email' | 'whatsapp' | null>(null);
  const [bookingStep, setBookingStep] = useState(0);
  const [params, setParams] = useState<Partial<EmailParams>>({
    companyName: '',
    jobTitle: '',
    responsibilities: '',
    recruiterName: '',
    recruiterEmail: '',
    recruiterPhone: ''
  });
  const [copied, setCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const {
    subject,
    body
  } = generateRecruiterEmail(params);
  // Booking preview steps
  const bookingSteps = [{
    icon: Calendar,
    title: t('connect.modal.booking.steps.calendar.title', {
      defaultValue: "You'll be taken to Google Calendar"
    }),
    description: t('connect.modal.booking.steps.calendar.desc', {
      defaultValue: "A new tab will open with Mona's booking page"
    })
  }, {
    icon: Clock,
    title: t('connect.modal.booking.steps.choose.title', {
      defaultValue: 'Choose a 30-minute slot'
    }),
    description: t('connect.modal.booking.steps.choose.desc', {
      defaultValue: 'Select a time that works best for you'
    })
  }, {
    icon: Video,
    title: t('connect.modal.booking.steps.meet.title', {
      defaultValue: "You'll receive a Google Meet link"
    }),
    description: t('connect.modal.booking.steps.meet.desc', {
      defaultValue: 'Confirmation email with video call details'
    })
  }];
  // Track modal open/close
  useEffect(() => {
    if (isOpen) {
      trackEvent(EVENTS.CONNECT_MODAL_OPENED);
    } else {
      trackEvent(EVENTS.CONNECT_MODAL_CLOSED);
    }
  }, [isOpen]);
  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
        setSelectedOption(null);
        setBookingStep(0);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(null);
      setBookingStep(0);
      setCopied(false);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleBookingClick = () => {
    setSelectedOption('booking');
    triggerSubtleConfetti(document.getElementById('booking-option'));
    trackEvent(EVENTS.CALENDAR_BOOKING_STARTED, {
      source: 'connect_modal'
    });
    let step = 0;
    const interval = setInterval(() => {
      step += 1;
      setBookingStep(step);
      if (step >= bookingSteps.length - 1) {
        clearInterval(interval);
        setTimeout(() => {
          trackEvent(EVENTS.CALENDAR_BOOKING_OPENED, {
            source: 'connect_modal'
          });
          window.open(CALENDAR_URL, '_blank');
          onClose();
        }, 1500);
      }
    }, 1200);
  };
  const handleEmailClick = () => {
    setSelectedOption('email');
    triggerSubtleConfetti(document.getElementById('email-option'));
    trackEvent(EVENTS.EMAIL_CLICKED, {
      source: 'connect_modal',
      flow: 'template_generator'
    });
  };
  const handleWhatsAppClick = () => {
    setSelectedOption('whatsapp');
    triggerSubtleConfetti(document.getElementById('whatsapp-option'));
    trackEvent(EVENTS.WHATSAPP_CLICKED, {
      source: 'connect_modal',
      flow: 'template_generator'
    });
    const message = `Hi Mona, I came across your profile and would like to discuss a job opportunity we have at:
- Company Name: [Your Company]
- Job Title: [Position Title]
- Compensation: [Salary Range]

Next Steps:
1. Please reply with your availability for a brief call
2. Share any questions you have about the role
3. We can schedule a formal interview

Looking forward to connecting!`;
    const whatsappUrl = createWhatsAppUrl(WHATSAPP_PHONE, message);
    window.open(whatsappUrl, '_blank');
    onClose();
  };
  const handleSendEmail = () => {
    triggerSubtleConfetti(document.getElementById('send-email-btn'));
    trackEvent(EVENTS.EMAIL_CLICKED, {
      source: 'connect_modal',
      action: 'send_with_template',
      has_customization: !!(params.companyName || params.jobTitle)
    });
    openMailClient(TO_EMAIL, subject, body);
  };
  const handleCopyDraft = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      setCopied(true);
      triggerSubtleConfetti(document.getElementById('copy-btn'));
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      alert("Couldn't copy to clipboard. Please copy the text manually.");
    }
  };
  const handleBack = () => {
    setSelectedOption(null);
    setBookingStep(0);
  };
  // Main choice screen
  if (!selectedOption) {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="connect-modal-title" aria-describedby="connect-modal-subtitle" data-id="element-162">
        <div ref={modalRef} className="bg-white rounded-lg shadow-lg max-w-md w-full animate-fadeIn" data-id="element-163">
          <div className="p-6" data-id="element-164">
            <div className="flex justify-between items-center mb-4" data-id="element-165">
              <h3 id="connect-modal-title" className="text-xl font-medium text-charcoal" data-id="element-166">
                {t('connect.modal.title', {
                defaultValue: 'Connect with Mona'
              })}
              </h3>
              <button onClick={onClose} className="text-grey-400 hover:text-grey-600 transition-colors duration-200" aria-label={t('connect.modal.close', {
              defaultValue: 'Close'
            })} data-id="element-167">
                <X className="h-5 w-5" data-id="element-168" />
              </button>
            </div>
            <p id="connect-modal-subtitle" className="text-grey-600 text-sm mb-6" data-id="element-169">
              {t('connect.modal.subtitle', {
              defaultValue: "Choose how you'd like to connect. Booking a video call is the fastest way to discuss opportunities."
            })}
            </p>
            <div className="space-y-3" data-id="element-170">
              {/* Primary: Book Video Interview */}
              <button id="booking-option" onClick={handleBookingClick} className="w-full bg-sage-mint hover:bg-sage-mint/90 text-white rounded-lg p-4 transition-all duration-300 hover:shadow-glow-mint hover:scale-[1.02] active:scale-98 group" aria-label={t('connect.modal.booking.title', {
              defaultValue: 'Book video interview'
            })} data-id="element-171">
                <div className="flex items-start" data-id="element-172">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-white/30 transition-all duration-300" data-id="element-173">
                    <Calendar className="h-6 w-6" data-id="element-174" />
                  </div>
                  <div className="text-left flex-1" data-id="element-175">
                    <div className="flex items-center justify-between mb-1" data-id="element-176">
                      <h4 className="font-medium text-lg" data-id="element-177">
                        {t('connect.modal.booking.title', {
                        defaultValue: 'Book video interview'
                      })}
                      </h4>
                      <Sparkles className="h-4 w-4 opacity-70" data-id="element-178" />
                    </div>
                    <p className="text-sm text-white/90" data-id="element-179">
                      {t('connect.modal.booking.subtitle', {
                      defaultValue: 'Schedule a 30-min Google Meet call'
                    })}
                    </p>
                    <div className="flex items-center mt-2 text-xs text-white/80" data-id="element-180">
                      <Clock className="h-3 w-3 mr-1" data-id="element-181" />
                      <span data-id="element-182">
                        {t('connect.modal.booking.available', {
                        defaultValue: 'Available slots this week'
                      })}
                      </span>
                    </div>
                  </div>
                </div>
              </button>

              {/* Secondary: Send Email */}
              <button id="email-option" onClick={handleEmailClick} className="w-full bg-white hover:bg-grey-400/5 text-charcoal border border-grey-400/30 rounded-lg p-4 transition-all duration-300 hover:border-sage-mint/50 hover:scale-[1.01] active:scale-98 group" aria-label={t('connect.modal.email.title', {
              defaultValue: 'Send email instead'
            })} data-id="element-183">
                <div className="flex items-start" data-id="element-184">
                  <div className="w-10 h-10 rounded-full bg-sage-mint/10 flex items-center justify-center mr-3 flex-shrink-0 group-hover:bg-sage-mint/20 transition-all duration-300" data-id="element-185">
                    <Mail className="h-5 w-5 text-sage-mint" data-id="element-186" />
                  </div>
                  <div className="text-left flex-1" data-id="element-187">
                    <h4 className="font-medium" data-id="element-188">
                      {t('connect.modal.email.title', {
                      defaultValue: 'Send email instead'
                    })}
                    </h4>
                    <p className="text-sm text-grey-600" data-id="element-189">
                      {t('connect.modal.email.subtitle', {
                      defaultValue: 'Quick introduction message'
                    })}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-grey-400 group-hover:text-sage-mint transition-colors duration-300" data-id="element-190" />
                </div>
              </button>

              {/* Tertiary: WhatsApp */}
              <button id="whatsapp-option" onClick={handleWhatsAppClick} className="w-full bg-white hover:bg-grey-400/5 text-charcoal border border-grey-400/30 rounded-lg p-4 transition-all duration-300 hover:border-sage-mint/50 hover:scale-[1.01] active:scale-98 group" aria-label={t('connect.modal.whatsapp.title', {
              defaultValue: 'Chat on WhatsApp'
            })} data-id="element-191">
                <div className="flex items-start" data-id="element-192">
                  <div className="w-10 h-10 rounded-full bg-sage-mint/10 flex items-center justify-center mr-3 flex-shrink-0 group-hover:bg-sage-mint/20 transition-all duration-300" data-id="element-193">
                    <MessageCircle className="h-5 w-5 text-sage-mint" data-id="element-194" />
                  </div>
                  <div className="text-left flex-1" data-id="element-195">
                    <h4 className="font-medium" data-id="element-196">
                      {t('connect.modal.whatsapp.title', {
                      defaultValue: 'Chat on WhatsApp'
                    })}
                    </h4>
                    <p className="text-sm text-grey-600" data-id="element-197">
                      {t('connect.modal.whatsapp.subtitle', {
                      defaultValue: 'Direct message'
                    })}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-grey-400 group-hover:text-sage-mint transition-colors duration-300" data-id="element-198" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Booking preview flow
  if (selectedOption === 'booking') {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-199">
        <div ref={modalRef} className="bg-white rounded-lg shadow-lg max-w-md w-full animate-fadeIn" data-id="element-200">
          <div className="p-6" data-id="element-201">
            <div className="flex items-center mb-6" data-id="element-202">
              <button onClick={handleBack} className="text-grey-400 hover:text-grey-600 transition-colors duration-200 mr-3" data-id="element-203">
                <ArrowRight className="h-5 w-5 rotate-180" data-id="element-204" />
              </button>
              <h3 className="text-lg font-medium text-charcoal" data-id="element-205">
                {t('connect.modal.booking.title')}
              </h3>
            </div>
            {/* Progress bar */}
            <div className="mb-6" data-id="element-206">
              <div className="h-1 bg-grey-400/20 rounded-full overflow-hidden" data-id="element-207">
                <div className="h-1 bg-sage-mint transition-all duration-500 rounded-full" style={{
                width: `${(bookingStep + 1) / bookingSteps.length * 100}%`
              }} data-id="element-208" />
              </div>
            </div>
            {/* Current step */}
            <div className="bg-sage-mint/5 rounded-lg p-6 mb-6" data-id="element-209">
              <div className="flex items-start" data-id="element-210">
                <div className="w-12 h-12 rounded-full bg-sage-mint/20 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-211">
                  {createElement(bookingSteps[bookingStep].icon, {
                  className: 'h-6 w-6 text-sage-mint'
                })}
                </div>
                <div data-id="element-212">
                  <h4 className="text-charcoal font-medium mb-2" data-id="element-213">
                    {bookingSteps[bookingStep].title}
                  </h4>
                  <p className="text-grey-600 text-sm" data-id="element-214">
                    {bookingSteps[bookingStep].description}
                  </p>
                </div>
              </div>
            </div>
            {/* All steps preview */}
            <div className="space-y-3" data-id="element-215">
              {bookingSteps.map((step, index) => <div key={index} className={`flex items-center p-3 rounded-lg transition-all duration-300 ${index <= bookingStep ? 'bg-sage-mint/10' : 'bg-grey-400/5'}`} data-id="element-216">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 transition-all duration-300 ${index <= bookingStep ? 'bg-sage-mint text-white' : 'bg-grey-400/20 text-grey-400'}`} data-id="element-217">
                    {index < bookingStep ? <CheckCircle className="h-4 w-4" data-id="element-218" /> : createElement(step.icon, {
                  className: 'h-4 w-4'
                })}
                  </div>
                  <div className="flex-1" data-id="element-219">
                    <p className={`text-sm font-medium ${index <= bookingStep ? 'text-charcoal' : 'text-grey-400'}`} data-id="element-220">
                      {step.title}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>;
  }
  // Email flow
  if (selectedOption === 'email') {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-221">
        <div ref={modalRef} className="bg-white rounded-lg shadow-lg max-w-xl w-full max-h-[90vh] overflow-auto animate-fadeIn" data-id="element-222">
          <div className="p-6" data-id="element-223">
            <div className="flex items-center mb-4" data-id="element-224">
              <button onClick={handleBack} className="text-grey-400 hover:text-grey-600 transition-colors duration-200 mr-3" data-id="element-225">
                <ArrowRight className="h-5 w-5 rotate-180" data-id="element-226" />
              </button>
              <h3 className="text-lg font-medium text-charcoal" data-id="element-227">
                {t('connect.modal.email.title')}
              </h3>
            </div>
            <p className="text-grey-600 text-sm mb-6" data-id="element-228">
              {t('connect.modal.subtitle')}
            </p>
            <div className="space-y-4" data-id="element-229">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-230">
                <div data-id="element-231">
                  <label htmlFor="companyName" className="block text-sm font-medium text-grey-600 mb-1" data-id="element-232">
                    {t('connect.modal.email.form.company')}
                  </label>
                  <input id="companyName" type="text" className="w-full px-3 py-2 border border-grey-400/30 rounded-lg focus:outline-none focus:ring-1 focus:ring-sage-mint focus:border-sage-mint" placeholder={t('connect.modal.email.form.companyPlaceholder')} value={params.companyName || ''} onChange={e => setParams(prev => ({
                  ...prev,
                  companyName: e.target.value
                }))} data-id="element-233" />
                </div>
                <div data-id="element-234">
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-grey-600 mb-1" data-id="element-235">
                    {t('connect.modal.email.form.position')}
                  </label>
                  <input id="jobTitle" type="text" className="w-full px-3 py-2 border border-grey-400/30 rounded-lg focus:outline-none focus:ring-1 focus:ring-sage-mint focus:border-sage-mint" placeholder={t('connect.modal.email.form.positionPlaceholder')} value={params.jobTitle || ''} onChange={e => setParams(prev => ({
                  ...prev,
                  jobTitle: e.target.value
                }))} data-id="element-236" />
                </div>
              </div>
              <div data-id="element-237">
                <label htmlFor="responsibilities" className="block text-sm font-medium text-grey-600 mb-1" data-id="element-238">
                  Key responsibilities
                </label>
                <input id="responsibilities" type="text" className="w-full px-3 py-2 border border-grey-400/30 rounded-lg focus:outline-none focus:ring-1 focus:ring-sage-mint focus:border-sage-mint" placeholder="Patient care, medication administration, team coordination" value={params.responsibilities || ''} onChange={e => setParams(prev => ({
                ...prev,
                responsibilities: e.target.value
              }))} data-id="element-239" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-240">
                <div data-id="element-241">
                  <label htmlFor="recruiterName" className="block text-sm font-medium text-grey-600 mb-1" data-id="element-242">
                    Your name
                  </label>
                  <input id="recruiterName" type="text" className="w-full px-3 py-2 border border-grey-400/30 rounded-lg focus:outline-none focus:ring-1 focus:ring-sage-mint focus:border-sage-mint" placeholder="Jane Doe" value={params.recruiterName || ''} onChange={e => setParams(prev => ({
                  ...prev,
                  recruiterName: e.target.value
                }))} data-id="element-243" />
                </div>
                <div data-id="element-244">
                  <label htmlFor="recruiterEmail" className="block text-sm font-medium text-grey-600 mb-1" data-id="element-245">
                    Your email
                  </label>
                  <input id="recruiterEmail" type="email" className="w-full px-3 py-2 border border-grey-400/30 rounded-lg focus:outline-none focus:ring-1 focus:ring-sage-mint focus:border-sage-mint" placeholder="recruiter@citycare.sg" value={params.recruiterEmail || ''} onChange={e => setParams(prev => ({
                  ...prev,
                  recruiterEmail: e.target.value
                }))} data-id="element-246" />
                </div>
              </div>
              <div data-id="element-247">
                <label htmlFor="recruiterPhone" className="block text-sm font-medium text-grey-600 mb-1" data-id="element-248">
                  Your phone (optional)
                </label>
                <input id="recruiterPhone" type="text" className="w-full px-3 py-2 border border-grey-400/30 rounded-lg focus:outline-none focus:ring-1 focus:ring-sage-mint focus:border-sage-mint" placeholder="+65 9123 4567" value={params.recruiterPhone || ''} onChange={e => setParams(prev => ({
                ...prev,
                recruiterPhone: e.target.value
              }))} data-id="element-249" />
              </div>
              <div className="mt-4 border border-grey-400/20 rounded-lg p-4 bg-cream-white/50" data-id="element-250">
                <h4 className="text-sm font-medium text-charcoal mb-2" data-id="element-251">
                  Email Preview
                </h4>
                <div className="text-sm text-grey-600" data-id="element-252">
                  <div className="font-medium" data-id="element-253">Subject: {subject}</div>
                  <div className="mt-2 whitespace-pre-wrap" data-id="element-254">{body}</div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-6" data-id="element-255">
                <button id="send-email-btn" onClick={handleSendEmail} className="liquid-glass-button relative overflow-hidden bg-sage-mint text-white rounded-lg px-6 py-3 transition-all duration-300 transform hover:scale-105 hover:shadow-glow-mint active:scale-98 flex items-center justify-center" data-id="element-256">
                  <Mail className="h-4 w-4 mr-2" data-id="element-257" />
                  <span data-id="element-258">{t('connect.modal.email.actions.send')}</span>
                </button>
                <button id="copy-btn" onClick={handleCopyDraft} className="liquid-glass-button relative overflow-hidden bg-white text-charcoal border border-grey-400/30 rounded-lg px-6 py-3 transition-all duration-300 transform hover:scale-105 hover:bg-grey-400/10 active:scale-98 flex items-center justify-center" data-id="element-259">
                  {copied ? <>
                      <CheckCircle className="h-4 w-4 mr-2 text-sage-mint" data-id="element-260" />
                      <span data-id="element-261">{t('connect.modal.email.actions.copied')}</span>
                    </> : <>
                      <Copy className="h-4 w-4 mr-2" data-id="element-262" />
                      <span data-id="element-263">{t('connect.modal.email.actions.copy')}</span>
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // WhatsApp flow
  if (selectedOption === 'whatsapp') {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-264">
        <div ref={modalRef} className="bg-white rounded-lg shadow-lg max-w-md w-full animate-fadeIn" data-id="element-265">
          <div className="p-6" data-id="element-266">
            <div className="flex items-center mb-4" data-id="element-267">
              <button onClick={handleBack} className="text-grey-400 hover:text-grey-600 transition-colors duration-200 mr-3" data-id="element-268">
                <ArrowRight className="h-5 w-5 rotate-180" data-id="element-269" />
              </button>
              <h3 className="text-lg font-medium text-charcoal" data-id="element-270">
                {t('connect.modal.whatsapp.title')}
              </h3>
            </div>
            <div className="bg-sage-mint/5 rounded-lg p-6 mb-4" data-id="element-271">
              <div className="flex items-start" data-id="element-272">
                <div className="w-12 h-12 rounded-full bg-sage-mint/20 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-273">
                  <MessageCircle className="h-6 w-6 text-sage-mint" data-id="element-274" />
                </div>
                <div data-id="element-275">
                  <h4 className="text-charcoal font-medium mb-2" data-id="element-276">
                    {t('connect.modal.whatsapp.opening')}
                  </h4>
                  <p className="text-grey-600 text-sm" data-id="element-277">
                    {t('connect.modal.whatsapp.description')}
                  </p>
                </div>
              </div>
            </div>
            <p className="text-grey-600 text-sm text-center" data-id="element-278">
              {t('connect.modal.whatsapp.popup')}
            </p>
          </div>
        </div>
      </div>;
  }
  return null;
};
export default ConnectModal;